// The period is one object with one key per axis — the same shape as REPORTS.pickup_dates, where the
// key that carries data IS the kind of date being asked for:
//
//   { seasons: [{ seasonFrom, seasonTo }], leadTimes: [{ leadFrom, leadTo }], weekdays: [1..7] }
//
// `seasons` and `leadTimes` are the two **modes** and are exclusive by construction: there is no
// state where a rule has both, and no separate `mode` field that could drift from the data —
// whichever of the two is filled IS the mode.
//
// `weekdays` is not a mode. It is an orthogonal **filter** that narrows whichever mode is active
// (Curro, 2026-09-19): before, "Saturdays in August" was unsayable because the two competed for the
// same slot. Empty means every day. The backend has always compared it as an independent axis
// (`rules.py` overlap algebra), so this is the storage catching up with the comparison.
//
// Weekdays are ISO: **1 = Monday … 7 = Sunday**, which is what the API validates and what the engine
// compares against `date.isoweekday()`. Not the 0-based numbering the reports filter uses.
//
// This lives in its own module, not in Period.jsx, because the period blocks import it and
// Period.jsx imports them: sharing it through the component would be an import cycle.
export const PERIOD_MODE = { SEASON: 'season', LEAD_TIME: 'leadTime' }

// Only the modes. `weekdays` is deliberately absent: switching mode must not wipe the filter.
export const PERIOD_MODE_FIELD = {
	[PERIOD_MODE.SEASON]: 'seasons',
	[PERIOD_MODE.LEAD_TIME]: 'leadTimes',
}

export const emptyPeriod = () => ({ seasons: [], leadTimes: [], weekdays: [] })

export const readPeriod = data => ({ ...emptyPeriod(), ...(data?.period ?? {}) })

// Writes one key of the period, leaving the others alone (the mode toggle is what empties them).
// Callers pass the whole drawer data so this stays the only place that knows the shape.
export const withPeriodField = (data, field, value) => ({
	...data,
	period: { ...readPeriod(data), [field]: value },
})

// The data says which mode it is: whichever of the two is filled. Weekdays says nothing about the
// mode — a rule can be a season narrowed to weekends, and that is still a season rule.
export const periodMode = data => {
	const period = readPeriod(data)
	if (period.leadTimes.length) return PERIOD_MODE.LEAD_TIME
	return PERIOD_MODE.SEASON
}